/* eslint-disable no-sparse-arrays */
import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import {
	Link,
	useHistory,
	useLocation,
	useParams
} from 'react-router-dom';
import {
	Header,
	Icon,
	Tab
} from 'semantic-ui-react';

import { log } from '../../../../../../services/utils';

import useForm from '../../../../../../hooks/useForm';

import {
	getTemplate,
	updateTemplate,
	createTemplate
} from '../../../../../../api/nora/programs';

import {
	useProgramState,
	useProgramAction
} from '../../../../../../contexts/ProgramsContext';
import { useUIAction } from '../../../../../../contexts/UIContext';

import Alert from '../../../../../Alert';
import InfoTab from './InfoTab';
import LayoutTab from './LayoutTab';

const TemplateDetails = () => {
	const history = useHistory();
	const params = useParams();
	const { state: navigationState } = useLocation();

	const {
		override,
		program,
		template
	} = useProgramState();
	const {
		setTemplate,
		clearTemplate
	} = useProgramAction();
	const {
		setApiErrorAlert,
		setInfobar,
		setInfobarLoader,
		setNavLock
	} = useUIAction();

	const [ isLoading, setIsLoading ] = useState( true );
	const [ forceValidation, setForceValidation ] = useState( false );
	const [ activeIndex, setActiveIndex ] = useState( navigationState
		? navigationState.activeIndex
		: 0 );
	const [ unsavedProgressAlert, setUnsavedProgressAlert ] = useState( false );
	const [ templateSavedAlert, setTemplateSavedAlert ] = useState( false );
	// used to store the tab index when showing the unsavedProgressAlert
	const [ pendingIndex /* setPendingIndex */ ] = useState();

	const {
		formData,
		hasChanged: formDataHasChanged,
		updateField,
		resetFormData,
		setFormData
	} = useForm( {} );

	useEffect( () => {
		navigationState?.to
			? setInfobar( <span><Link to={ navigationState.to }>{ navigationState.text || '\u2190\u00A0\u00A0Back' }</Link></span> )
			: override.breadcrumb
				? setInfobar( <>
					{ override.breadcrumb }
					<span>&nbsp;&#9656;&nbsp; <Link to={ { pathname: override.path, state: { activeIndex: 1 } } }>Templates</Link> &nbsp;&#9656;&nbsp; { template?.name ? template.name : 'New Template' }</span>
				</> )
				: setInfobar( <span>Operations &nbsp;&#9656;&nbsp; <Link to="/operations/programs">Programs</Link> &nbsp;&#9656;&nbsp; <Link to={ `/operations/programs/${ program.id }` }>{ program?.name ? program.name : 'New Program' }</Link> &nbsp;&#9656;&nbsp; <Link to={ { pathname: `/operations/programs/${ program.id }`, state: { activeIndex: 4 } } }>Templates</Link> &nbsp;&#9656;&nbsp; { template?.name ? template.name : 'New Template' }</span> );
	}, [
		navigationState,
		override.breadcrumb,
		override.path,
		program,
		setInfobar,
		template
	] );

	useEffect( () => {
		if ( params.templateId > 0 ) { // not a new template
			( async () => {
				setInfobarLoader( 'Retrieving Template Info...' );

				try {
					const response = await getTemplate( program.id, params.templateId );

					setTemplate( response );
				} catch ( error ) {
					setApiErrorAlert( error );
				}
			} )();
		}

		return () => {
			clearTemplate();
		};
	}, [
		clearTemplate,
		setInfobarLoader,
		setApiErrorAlert,
		params.templateId,
		program.id,
		setTemplate
	] );

	useEffect( () => {
		if ( formDataHasChanged ) {
			setNavLock( true );
		} else {
			setNavLock( false );
		}
	}, [
		formDataHasChanged,
		setNavLock
	] );

	useEffect( () => {
		if ( params.templateId === '0' ) {
			setFormData( {
				id: 0,
				layout: [],
				occurrence: 13,
				status: 16
			} ); // default status to 16 for new docs
		} else if ( template ) {
			setFormData( {
				...template,
				occurrence: template.occurrence.id,
				type: template.type.id,
				status: template.status.id
			} );
		}
	}, [
		template,
		params.templateId,
		setFormData
	] );

	useEffect( () => {
		if ( Object.keys( formData ).length ) {
			setIsLoading( false );
			setInfobarLoader( '' );
		}
	}, [
		formData,
		setInfobarLoader
	] );

	const infoErrors = useMemo( () => ( {
		name: [
			{
				error: !formData.name,
				message: 'Name is a required field.',
				fieldLabel: 'Name'
			}
		],
		occurence: [
			{
				error: !formData.occurrence,
				message: 'Occurrence is a required field.',
				fieldLabel: 'Occurrence'
			}
		],
		type: [
			{
				error: !formData.type,
				message: 'Type is a required field.',
				fieldLabel: 'Type'
			}
		],
		status: [
			{
				error: !formData.status,
				message: 'Status is a required field.',
				fieldLabel: 'Status'
			}
		],
		open: [
			{
				error: !formData.open,
				message: 'Open Date is a required field.',
				fieldLabel: 'Open Date'
			},
			{
				error: formData.open
					&& !formData.id
					&& new Date( formData.open ).setHours( 0, 0, 0, 0 ) < new Date().setHours( 0, 0, 0, 0 ),
				message: "Open Date may not proceed today's date.",
				fieldLabel: 'Open Date'
			}
		],
		due: [
			{
				error: formData.type !== 98 && !formData.due,
				message: 'Due Date is a required field.',
				fieldLabel: 'Due Date'
			},
			{
				error: formData.type !== 98 && formData.due
					&& formData.open
					&& new Date( formData.due ).setHours( 0, 0, 0, 0 ) <= new Date( formData.open ).setHours( 0, 0, 0, 0 ),
				message: 'Due Date must succeed Open Date by at least one calendar day.',
				fieldLabel: 'Due Date'
			}
		],
		description: [
			{
				error: !formData.description,
				message: 'Description is a required field.',
				fieldLabel: 'Description'
			}
		],
		introduction: [
			{
				error: !formData.introduction,
				message: 'Instructions is a required field.',
				fieldLabel: 'Instructions'
			}
		]
	} ), [
		formData.description,
		formData.due,
		formData.id,
		formData.introduction,
		formData.name,
		formData.occurrence,
		formData.open,
		formData.status,
		formData.type
	] );

	const layoutErrors = useMemo( () => ( {
		layout: [
			{
				error: formData.type !== 100 && !formData.layout?.length,
				message: 'At least one layout module is required.',
				fieldLabel: 'Layout'
			}
		]
	} ), [
		formData.layout?.length,
		formData.type
	] );

	const validate = useCallback( errors => {
		return Object.values( errors ).some( errs => errs.some( err => err.error ) );
	}, [] );

	const handleSave = useCallback( async cb => {
		if ( validate( { ...infoErrors, ...layoutErrors } ) ) {
			setForceValidation( true );
		} else {
			setForceValidation( false );

			let {
				occurrence,
				status,
				type,
				...rest
			} = formData;
			const data = {
				...rest,
				occurrence: { id: occurrence },
				status: { id: status },
				type: { id: type }
			};

			try {
				setNavLock( false );

				let newId = 0;

				if ( formData.id === 0 ) {
					const response = await createTemplate( program.id, data );

					newId = response.id;

					setTemplate( response );
				} else {
					const response = await updateTemplate( program.id, data );

					setTemplate( response );
				}

				setTemplateSavedAlert( true );

				cb?.();

				if ( newId ) {
					history.push( `/operations/programs/${ program.id }/templates/${ newId }` );
				}
			} catch ( error ) {
				setNavLock( true );
				setApiErrorAlert( error );
			}
		}
	}, [
		validate,
		infoErrors,
		layoutErrors,
		formData,
		setNavLock,
		program.id,
		setTemplate,
		history,
		setApiErrorAlert
	] );

	const menu = useMemo( () => ( {
		className: 'no-print',
		pointing: true,
		secondary: true,
		floated: true,
		style: {
			width: '100%',
			paddingTop: '0.5rem',
			marginBottom: '3rem'
		}
	} ), [] );

	const renderInfoTab = useCallback( () => (
		<InfoTab
			errors={ { ...infoErrors, ...layoutErrors } }
			forceValidation={ forceValidation }
			formData={ formData }
			formDataHasChanged= {formDataHasChanged }
			handleSave={ handleSave }
			resetFormData={ resetFormData }
			updateField={ updateField }
			validate={ () => validate( { ...infoErrors, ...layoutErrors } ) }
		/>
	), [
		forceValidation,
		formData,
		formDataHasChanged,
		handleSave,
		infoErrors,
		layoutErrors,
		resetFormData,
		updateField,
		validate
	] );

	const renderLayoutTab = useCallback( () => (
		<LayoutTab
			errors={ { ...infoErrors, ...layoutErrors } }
			forceValidation={ forceValidation }
			formData={ formData }
			formDataHasChanged= {formDataHasChanged }
			handleSave={ handleSave }
			resetFormData={ resetFormData }
			updateField={ updateField }
			validate={ () => validate( { ...infoErrors, ...layoutErrors } ) }
		/>
	), [
		forceValidation,
		formData,
		formDataHasChanged,
		handleSave,
		infoErrors,
		layoutErrors,
		resetFormData,
		updateField,
		validate
	] );

	const renderPanes = useCallback( () => {
		let panes = [
			{
				menuItem: {
					key: 'info',
					content: <>Info&nbsp;{ forceValidation && validate( infoErrors )
						? <Icon color="red" name="exclamation circle" />
						: null
					}</>
				},
				render: renderInfoTab
			},
			...formData.type !== 100
				? [
					{
						menuItem: {
							key: 'layout',
							content: <>Modules&nbsp;{ forceValidation && validate( layoutErrors )
								? <Icon color="red" name="exclamation circle" />
								: null
							}</>
						},
						render: renderLayoutTab
					}
				]
				: []
		];

		return panes;
	}, [
		forceValidation,
		formData.type,
		infoErrors,
		layoutErrors,
		renderInfoTab,
		renderLayoutTab,
		validate
	] );

	const handleUnsavedProgress = useCallback( async () => {
		await handleSave();
		setUnsavedProgressAlert( false );
		setActiveIndex( pendingIndex );
	}, [
		handleSave,
		pendingIndex
	] );

	const closeUnsavedProgressAlert = useCallback( () => {
		setUnsavedProgressAlert( false );
	}, [] );

	const closeProgramSavedAlert = useCallback( () => {
		setTemplateSavedAlert( false );
	}, [] );

	const handleTabChange = useCallback( ( e, { activeIndex } ) => {
		// if ( !validate() ) {
		// 	if ( forceValidation ) {
		// 		setForceValidation( false );
		// 	}

		// 	if ( formDataHasChanged ) {
		// 		setPendingIndex( activeIndex );
		// 		setUnsavedProgressAlert( true );
		// 	} else {
		setActiveIndex( activeIndex );
		// 	}
		// } else if ( !forceValidation ) {
		// 	setForceValidation( true );
		// }
	}, [] );
		// forceValidation,
		// formDataHasChanged,
		// validate
	// ] );

	if ( isLoading ) {
		return null;
	}

	return (
		<div className="detail-page">
			{ log( 'templateDetails' ) }
			<Tab
				activeIndex={ activeIndex }
				menu={ menu }
				onTabChange={ handleTabChange }
				panes={ renderPanes() }
			/>
			<Alert
				action={ handleUnsavedProgress }
				actionText="Save and continue"
				name="template-unsaved-progress-confirm-alert"
				message="Please save your progress before continuing."
				open={ unsavedProgressAlert }
				closeModal={ closeUnsavedProgressAlert }
				closeText="Go back"
				type="confirm"
			/>
			<Alert
				closeModal={ closeProgramSavedAlert }
				message={ <Header as="h1" color="pink">Saved!</Header> }
				name="template-saved-notification"
				open={ templateSavedAlert }
				type="notification"
			/>
		</div>
	);
};

export default memo( TemplateDetails );
